import { type MutableRefObject, type Ref, type RefObject } from 'react';

import { css } from '@compiled/react';

import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu-next';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import LozengeDropdownTrigger from '@atlaskit/lozenge-next/lozenge-dropdown-trigger';
import Lozenge from '@atlaskit/lozenge-next/new/lozenge';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import {
	ENGINEERING_WORKFLOW,
	type EngineeringWorkflowStatus,
} from './jsm-playground-engineering-data';

const BACKLOG_STATUS = ENGINEERING_WORKFLOW[4];

const assignRef = <T,>(ref: Ref<T> | undefined, node: T | null) => {
	if (typeof ref === 'function') {
		ref(node);
	} else if (ref) {
		(ref as MutableRefObject<T | null>).current = node;
	}
};

const statusTriggerAppearance = (status: EngineeringWorkflowStatus) => {
	if (status.tone === 'blue') return 'accent-blue';
	if (status.tone === 'green') return 'success';
	if (status.tone === 'red') return 'accent-red';
	return 'accent-gray';
};

const StatusLozenge = ({
	showLabel,
	status,
}: {
	showLabel: boolean;
	status: EngineeringWorkflowStatus;
}) => {
	if (!showLabel) return <Skeleton height={10} width={80} />;
	return <Lozenge appearance={statusTriggerAppearance(status)}>{status.label}</Lozenge>;
};

const StatusTransition = ({ status }: { status: EngineeringWorkflowStatus }) => (
	<span css={statusTransitionStyles}>
		<ArrowRightIcon color={token('color.icon.subtle')} label="" size="small" />
		<StatusLozenge showLabel status={status} />
	</span>
);

export const EngineeringRowStatusDropdown = ({
	onSelect,
	showLabel,
	status,
}: {
	onSelect: (status: EngineeringWorkflowStatus) => void;
	showLabel: boolean;
	status: EngineeringWorkflowStatus;
}) => (
	<span css={rowStatusDropdownStyles}>
		<DropdownMenu
			placement="bottom-start"
			shouldRenderToParent
			strategy="absolute"
			testId="engineering-row-status"
			trigger={({ triggerRef, ...triggerProps }) => (
				<LozengeDropdownTrigger
					{...triggerProps}
					aria-label={status.label}
					ref={triggerRef}
					appearance={statusTriggerAppearance(status)}
				>
					{showLabel ? status.label : <Skeleton height={10} width={64} />}
				</LozengeDropdownTrigger>
			)}
		>
			<div css={statusMenuStyles}>
				<DropdownItemGroup>
					{ENGINEERING_WORKFLOW.filter(
						(workflowStatus) => workflowStatus.label !== status.label,
					).map((workflowStatus) => (
						<DropdownItem
							elemAfter={<StatusTransition status={workflowStatus} />}
							key={workflowStatus.label}
							onClick={() => onSelect(workflowStatus)}
						>
							{workflowStatus.label}
						</DropdownItem>
					))}
				</DropdownItemGroup>
			</div>
		</DropdownMenu>
	</span>
);

export const EngineeringStatusDropdown = ({
	backlogItemRef,
	isOpen,
	onOpenChange,
	onSelect,
	status,
	triggerRef,
}: {
	backlogItemRef: RefObject<HTMLDivElement>;
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
	onSelect: (status: EngineeringWorkflowStatus) => void;
	status: EngineeringWorkflowStatus;
	triggerRef: MutableRefObject<HTMLElement | null>;
}) => (
	<DropdownMenu<HTMLElement>
		isOpen={isOpen}
		onOpenChange={({ isOpen: nextIsOpen }) => onOpenChange(nextIsOpen)}
		placement="bottom-end"
		shouldRenderToParent
		strategy="absolute"
		testId="engineering-status"
		trigger={({ triggerRef: dropdownRef, ...triggerProps }) => (
			<LozengeDropdownTrigger
				{...triggerProps}
				aria-label={status.label}
				appearance="neutral"
				maxWidth="100%"
				ref={(node) => {
					assignRef(dropdownRef, node);
					triggerRef.current = node;
				}}
				spacing="spacious"
			>
				{status.label}
			</LozengeDropdownTrigger>
		)}
	>
		<div css={statusMenuStyles}>
			<DropdownItemGroup>
				{ENGINEERING_WORKFLOW.filter((workflowStatus) => workflowStatus.label !== status.label).map(
					(workflowStatus) => (
						<div
							key={workflowStatus.label}
							ref={workflowStatus.label === BACKLOG_STATUS.label ? backlogItemRef : undefined}
						>
							<DropdownItem
								elemAfter={<StatusTransition status={workflowStatus} />}
								onClick={() => onSelect(workflowStatus)}
							>
								{workflowStatus.label}
							</DropdownItem>
						</div>
					),
				)}
			</DropdownItemGroup>
		</div>
	</DropdownMenu>
);

const statusMenuStyles = css({
	width: '340px',
	// Status names and their transition lozenges remain a single scannable row.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[role="menuitem"]': {
		whiteSpace: 'nowrap',
	},
});
const rowStatusDropdownStyles = css({
	display: 'inline-flex',
});
const statusTransitionStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.150'),
});
